import React from "react";
import moment from "moment-timezone";
import { Trans, useTranslation } from "react-i18next";

// Products come from the backend, but the known set of nav items is fixed
// (matches dataMenu's own keys in Header.jsx), so a stable translation key
// derived the same way dataMenu looks itself up (strip spaces, lowercase)
// lets this API-driven list still be localized. Falls back to the
// API-provided name unchanged if a given product's key isn't in the catalog.
const productNameKey = (name) => `product_name_${String(name ?? "").replace(/\s+/g, "").toLowerCase()}`;

const HeaderNav = ({ Products, dataMenu, isAvaitor, hideMobile, showDesktop = true, showMobile = true }) => {
  const { t } = useTranslation();
  const handleClick = (f, foundData) => {
    if (f?.name === "Aviator") {
      isAvaitor("3193817929df4f8481eb141698d331f0");
    } else {
      foundData.route();
      foundData.dispatch();
    }
  };

  return (
    <>
      {/* Desktop (>=768px) — plain text nav links inline in the main header
          row (design-kit parity: nav sits alongside logo/balance/account in
          one row, not a separate bar below), gold text when active, no
          filled background box. */}
      {/* Desktop nav bar — rendered as a full-width bar BELOW the main header
          row (design-kit parity). Height 44 px, active item = gold fill + dark
          text, hover = translucent gold bg + gold text. Items are equally sized
          via flex: 1 1 0%. */}
      {showDesktop && (
        <nav
          className="!hidden md:!flex"
          style={{
            height: 44,
            alignItems: "stretch",
            background: "#0b131c",
            borderBottom: "1px solid var(--border-subtle)",
          }}
        >
          {Products?.map((f, index) => {
            const foundData = dataMenu[f?.name.replace(/\s+/g, "")];
            if (!foundData) return null;
            const isActive = !!foundData.className?.includes("active");

            return (
              <button
                key={index}
                type="button"
                onClick={() => handleClick(f, foundData)}
                style={{
                  flex: "1 1 0%",
                  padding: "0 var(--space-6, 12px)",
                  background: isActive ? "var(--gold-500)" : "transparent",
                  border: 0,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  transition: "background .15s, color .15s",
                  fontFamily: "var(--font-body)",
                  fontWeight: 800,
                  fontSize: "var(--fs-sm, 13px)",
                  color: isActive ? "#0b131c" : "var(--text-secondary)",
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = "var(--gold-a12)";
                    e.currentTarget.style.color = "var(--gold-400)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = "var(--text-secondary)";
                  }
                }}
              >
                {t(productNameKey(f?.name), f?.name)}
                {foundData?.badge && <span className="BLM-newBadge ml-1">{foundData.badge}</span>}
              </button>
            );
          })}
        </nav>
      )}

      {/* Mobile web (<768px) — matches the app's actual current dark theme */}
      {showMobile && !hideMobile && (
        <div
          className="md:hidden flex items-center gap-2 overflow-x-auto"
          style={{
            background: "var(--surface-inset)",
            borderTop: "1px solid var(--border-subtle)",
            borderBottom: "1px solid var(--border-subtle)",
            padding: "10px 16px",
          }}
        >
          {Products?.map((f, index) => {
            const foundData = dataMenu[f?.name.replace(/\s+/g, "")];
            if (!foundData) return null;
            const isActive = !!foundData.className?.includes("active");

            return (
              <button
                key={index}
                type="button"
                onClick={() => handleClick(f, foundData)}
                className="flex shrink-0 items-center justify-center gap-1"
                style={{
                  padding: "6px 14px",
                  minHeight: 30,
                  minWidth: 0,
                  borderRadius: 999,
                  // DS spec: active = gold-500 bg + on-brand text; inactive = surface-3 bg + transparent border + text-secondary
                  background: isActive ? "var(--gold-500)" : "var(--surface-3)",
                  border: `1px solid ${isActive ? "var(--gold-500)" : "transparent"}`,
                  cursor: "pointer",
                }}
              >
                <span
                  className="whitespace-nowrap text-xs"
                  style={{
                    color: isActive ? "var(--on-brand)" : "var(--text-secondary)",
                    fontWeight: 600,
                  }}
                >
                  {t(productNameKey(f?.name), f?.name)}
                </span>
                {/* RN Header.tsx hardcodes the NEW badge to LiveCasino only, independent of
                    any shared nav-item data */}
                {f?.name?.replace(/\s+/g, "") === "LiveCasino" && (
                  <span
                    className="whitespace-nowrap text-[8px] font-bold text-white"
                    style={{ background: "#ef4444", borderRadius: 999, padding: "1px 5px" }}
                  >
                    <Trans i18nKey="header_new_badge">NEW</Trans>
                  </span>
                )}
              </button>
            );
          })}
          <div className="flex shrink-0 items-center pl-3">
            <span className="whitespace-nowrap text-xs font-bold text-white">
              <Trans i18nKey="header_nav_wat">WAT +</Trans>
              {moment().tz("africa/lagos").format("HH:mm:ss")}
            </span>
          </div>
        </div>
      )}
    </>
  );
};

export default HeaderNav;
